import { FC, useState, useEffect, useRef } from 'react';
import {
  Questionnaire,
  QuestionnaireResponse,
  Patient,
  AllergyIntolerance,
} from '@medplum/fhirtypes';
import { Divider, Stack } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { ConditionsList } from '@/pages/ainigma/patient/examination/conditions/ConditionsList';
import { ConditionInputItem } from '@/utils/condition.utils';
import { SurgeryInputItem } from '@/utils/surgery.utils';
import { ProceduresList } from '@/pages/ainigma/patient/examination/procedures/ProceduresList';
import { useConditions } from '@/hooks/useConditions';
import { useProcedures } from '@/hooks/useProcedures';
import { AllergyList } from './allergies/AllergyList';
import { isEqual, cloneDeep } from 'lodash';
import { useAllergies } from '@/hooks/useAllergies';
import { MedicalHistorySections } from '../medical-history/components/MedicalHistorySections';

interface MedicalHistoryContentProps {
  questionnaire?: Questionnaire;
  questionnaireResponse?: QuestionnaireResponse | undefined;
  patient: Patient;
  onClose?: () => void;
  setIsDirty: (dirty: boolean) => void;
  readonly?: boolean;
  attachmentPreview?: boolean;
}

export const MedicalHistoryContent: FC<MedicalHistoryContentProps> = ({
  patient,
  setIsDirty,
  readonly = false,
}) => {
  const medplum = useMedplum();
  const { loadPatientAllergies } = useAllergies();
  const { loadPatientConditions } = useConditions();
  const { loadPatientProcedures } = useProcedures();
  const { currentEncounter } = useCurrentEncounter(patient);
  const [conditions, setConditions] = useState<ConditionInputItem[]>([]);
  const [surgeries, setSurgeries] = useState<SurgeryInputItem[]>([]);
  const [allergies, setAllergies] = useState<AllergyIntolerance[]>([]);
  const [loadingMedicalHistory, setLoadingMedicalHistory] = useState(false);

  // Store initial conditions, surgeries, and allergies state
  const initialConditionsRef = useRef<ConditionInputItem[]>([]);
  const initialSurgeriesRef = useRef<SurgeryInputItem[]>([]);
  const initialAllergiesRef = useRef<AllergyIntolerance[]>([]);

  // Force re-render keys for components
  const [conditionsInputKey] = useState(0);
  const [surgeriesInputKey] = useState(0);
  const [allergiesInputKey] = useState(0);
  const [isMedicalHistoryLoaded, setIsMedicalHistoryLoaded] = useState(false);

  // Load medical history conditions and surgeries
  useEffect(() => {
    if (!patient?.id || readonly) {
      return;
    }

    async function loadMedicalHistory() {
      setLoadingMedicalHistory(true);
      try {
        const [conditionsResult, proceduresResult, allergiesResult] =
          await Promise.all([
            loadPatientConditions(patient),
            loadPatientProcedures(patient),
            loadPatientAllergies(patient),
          ]);

        const loadedConditions = conditionsResult.conditions;
        const loadedSurgeries = proceduresResult.procedures;
        const loadedAllergies = allergiesResult.allergies;

        setConditions(loadedConditions);
        setSurgeries(loadedSurgeries);
        setAllergies(loadedAllergies);

        // Store initial state for change tracking
        initialConditionsRef.current = cloneDeep(loadedConditions);
        initialSurgeriesRef.current = cloneDeep(loadedSurgeries);
        initialAllergiesRef.current = cloneDeep(loadedAllergies);
        setIsMedicalHistoryLoaded(true);
      } catch (error) {
        console.error('Error loading medical history:', error);
      } finally {
        setLoadingMedicalHistory(false);
      }
    }

    loadMedicalHistory();
  }, [
    medplum,
    patient,
    readonly,
    loadPatientAllergies,
    loadPatientConditions,
    loadPatientProcedures,
  ]);

  // Effect to detect changes and update isDirty state
  useEffect(() => {
    if (readonly) {
      setIsDirty(false);
      return;
    }

    // Check conditions changes (only if medical history is loaded)
    const hasConditionsChanges = isMedicalHistoryLoaded
      ? !isEqual(conditions, initialConditionsRef.current)
      : false;

    // Check surgeries changes (only if medical history is loaded)
    const hasSurgeriesChanges = isMedicalHistoryLoaded
      ? !isEqual(surgeries, initialSurgeriesRef.current)
      : false;

    // Check allergies changes (only if medical history is loaded)
    const hasAllergiesChanges = isMedicalHistoryLoaded
      ? !isEqual(allergies, initialAllergiesRef.current)
      : false;

    const hasChanges =
      hasConditionsChanges || hasSurgeriesChanges || hasAllergiesChanges;
    setIsDirty(hasChanges);
  }, [
    conditions,
    surgeries,
    allergies,
    readonly,
    setIsDirty,
    isMedicalHistoryLoaded,
  ]);

  return (
    <>
      <MedicalHistorySections patient={patient} readonly={readonly} />

      {!loadingMedicalHistory && (
        <>
          <Divider my="lg" />
          <Stack gap="lg">
            <ConditionsList
              key={`conditions-${conditionsInputKey}`}
              conditions={conditions}
              onChange={setConditions}
              patient={patient}
              encounter={currentEncounter}
              isMedicalHistory={true}
              readonly={readonly}
            />
            <Divider />
            <ProceduresList
              key={`surgeries-${surgeriesInputKey}`}
              procedures={surgeries}
              onChange={setSurgeries}
              patient={patient}
              encounter={currentEncounter}
              isMedicalHistory={true}
              readonly={readonly}
            />
            <Divider />
            <AllergyList
              key={`allergies-${allergiesInputKey}`}
              allergies={allergies}
              onChange={setAllergies}
              patient={patient}
              encounter={currentEncounter}
              readonly={readonly}
            />
          </Stack>
        </>
      )}
    </>
  );
};
